import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { getAttendanceSummary, getEmployees, getDepartments } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Calendar, Users, Building2, CheckCircle, XCircle, Clock,
  Filter, Download, TrendingUp, Percent, ChevronDown, ChevronRight,
  UserCheck, UserX, AlertCircle, Search
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  present: 'bg-emerald-100 text-emerald-700',
  absent: 'bg-red-100 text-red-700',
  half_day: 'bg-amber-100 text-amber-700',
  on_leave: 'bg-blue-100 text-blue-700',
  holiday: 'bg-orange-100 text-orange-700',
  week_off: 'bg-purple-100 text-purple-700'
};

const AttendanceSummary = ({ user, setUser }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [filterEmployeeId, setFilterEmployeeId] = useState('all');
  const [filterDepartmentId, setFilterDepartmentId] = useState('all');
  
  // View states
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [dateFrom, dateTo, filterEmployeeId, filterDepartmentId]);

  const fetchInitialData = async () => {
    try {
      const [empRes, deptRes] = await Promise.all([
        getEmployees(),
        getDepartments()
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (filterEmployeeId !== 'all') params.employee_id = filterEmployeeId;
      if (filterDepartmentId !== 'all') params.department_id = filterDepartmentId;
      
      const response = await getAttendanceSummary(params);
      setSummaryData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Only Admin or Manager can view attendance summary.');
      } else {
        toast.error('Failed to fetch attendance summary');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!summaryData) return;
    
    let csv = 'Employee ID,Name,Department,Present,Absent,Half Day,On Leave,Holiday,Week Off,Total Days,Attendance %\n';
    
    summaryData.by_employee.forEach(emp => {
      csv += `${emp.employee_id},"${emp.name}","${emp.department}",${emp.present},${emp.absent},${emp.half_day},${emp.on_leave},${emp.holiday},${emp.week_off},${emp.total_days},${emp.attendance_percentage}%\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_summary_${dateFrom}_to_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Summary exported successfully');
  };

  const setQuickDateRange = (range) => {
    const today = new Date();
    let from, to;
    
    switch (range) {
      case 'today':
        from = to = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        from = to = yesterday.toISOString().split('T')[0];
        break;
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        from = startOfWeek.toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'lastWeek':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        from = lastWeekStart.toISOString().split('T')[0];
        to = lastWeekEnd.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        from = lastMonth.toISOString().split('T')[0];
        to = lastMonthEnd.toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    setDateFrom(from);
    setDateTo(to);
  };

  const hasAccess = useSectionAccess('/attendance-summary');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading && !summaryData) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Attendance Summary</h1>
            <p className="text-gray-500">
              {summaryData?.summary?.date_range?.from} to {summaryData?.summary?.date_range?.to}
            </p>
          </div>
          <Button onClick={exportToCSV} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {/* Quick Date Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick date buttons */}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setQuickDateRange('today')}>Today</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateRange('yesterday')}>Yesterday</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateRange('thisWeek')}>This Week</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateRange('lastWeek')}>Last Week</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateRange('thisMonth')}>This Month</Button>
              <Button variant="outline" size="sm" onClick={() => setQuickDateRange('lastMonth')}>Last Month</Button>
            </div>
            
            {/* Custom filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Date From</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Date To</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Employee</label>
                <Select value={filterEmployeeId} onValueChange={(val) => {
                  setFilterEmployeeId(val);
                  setEmployeeSearch('');
                }}>
                  <SelectTrigger data-testid="employee-filter">
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 pb-2 sticky top-0 bg-white z-10">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search employee..."
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          className="pl-8 h-9"
                          data-testid="employee-search-input"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees
                      .filter(emp => {
                        if (!employeeSearch) return true;
                        const searchLower = employeeSearch.toLowerCase();
                        const empName = (emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || '').toLowerCase();
                        const empId = (emp.employee_id || '').toLowerCase();
                        return empName.includes(searchLower) || empId.includes(searchLower);
                      })
                      .map(emp => {
                        const displayName = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.employee_id;
                        return (
                          <SelectItem key={emp.id} value={emp.id}>
                            {displayName} {emp.employee_id ? `(${emp.employee_id})` : ''}
                          </SelectItem>
                        );
                      })}
                    {employees.filter(emp => {
                      if (!employeeSearch) return true;
                      const searchLower = employeeSearch.toLowerCase();
                      const empName = (emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || '').toLowerCase();
                      const empId = (emp.employee_id || '').toLowerCase();
                      return empName.includes(searchLower) || empId.includes(searchLower);
                    }).length === 0 && (
                      <div className="py-4 text-center text-sm text-slate-500">
                        No employees found
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Department</label>
                <Select value={filterDepartmentId} onValueChange={setFilterDepartmentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overall Statistics */}
        {summaryData && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-blue-600 font-medium">Total Records</p>
                <p className="text-2xl font-bold text-blue-900">{summaryData.summary.total_records}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-emerald-600 font-medium">Present</p>
                <p className="text-2xl font-bold text-emerald-900">{summaryData.summary.status_breakdown.present}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-red-600 font-medium">Absent</p>
                <p className="text-2xl font-bold text-red-900">{summaryData.summary.status_breakdown.absent}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-amber-600 font-medium">Half Day</p>
                <p className="text-2xl font-bold text-amber-900">{summaryData.summary.status_breakdown.half_day}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-indigo-600 font-medium">On Leave</p>
                <p className="text-2xl font-bold text-indigo-900">{summaryData.summary.status_breakdown.on_leave}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-purple-600 font-medium">Holiday</p>
                <p className="text-2xl font-bold text-purple-900">{summaryData.summary.status_breakdown.holiday}</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-teal-600 font-medium">Attendance Rate</p>
                <p className="text-2xl font-bold text-teal-900">{summaryData.summary.overall_attendance_rate}%</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'overview' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('overview')}
          >
            <Users className="inline h-4 w-4 mr-1" /> By Employee
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'byDate' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('byDate')}
          >
            <Calendar className="inline h-4 w-4 mr-1" /> By Date
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'byDept' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
            onClick={() => setActiveTab('byDept')}
          >
            <Building2 className="inline h-4 w-4 mr-1" /> By Department
          </button>
        </div>

        {/* Tab Content */}
        {summaryData && activeTab === 'overview' && (
          <Card>
            <CardHeader>
              <CardTitle>Employee-wise Attendance ({summaryData.by_employee.length} employees)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Employee</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Department</th>
                      <th className="text-center p-3 text-sm font-medium text-emerald-600">Present</th>
                      <th className="text-center p-3 text-sm font-medium text-red-600">Absent</th>
                      <th className="text-center p-3 text-sm font-medium text-amber-600">Half Day</th>
                      <th className="text-center p-3 text-sm font-medium text-indigo-600">Leave</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Total</th>
                      <th className="text-center p-3 text-sm font-medium text-teal-600">Attendance %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {summaryData.by_employee.map(emp => (
                      <tr key={emp.employee_id} className="hover:bg-gray-50">
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{emp.name}</div>
                          <div className="text-xs text-gray-500">{emp.employee_id}</div>
                        </td>
                        <td className="p-3 text-sm text-gray-600">{emp.department}</td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm font-medium">{emp.present}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">{emp.absent}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm font-medium">{emp.half_day}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm font-medium">{emp.on_leave}</span>
                        </td>
                        <td className="p-3 text-center font-medium">{emp.total_days}</td>
                        <td className="p-3 text-center">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            emp.attendance_percentage >= 90 ? 'bg-emerald-100 text-emerald-700' :
                            emp.attendance_percentage >= 75 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {emp.attendance_percentage}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {summaryData && activeTab === 'byDate' && (
          <Card>
            <CardHeader>
              <CardTitle>Date-wise Attendance ({summaryData.by_date.length} days)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-center p-3 text-sm font-medium text-emerald-600">Present</th>
                      <th className="text-center p-3 text-sm font-medium text-red-600">Absent</th>
                      <th className="text-center p-3 text-sm font-medium text-amber-600">Half Day</th>
                      <th className="text-center p-3 text-sm font-medium text-indigo-600">On Leave</th>
                      <th className="text-center p-3 text-sm font-medium text-purple-600">Holiday</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Week Off</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {summaryData.by_date.map(day => (
                      <tr key={day.date} className="hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{day.date}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm font-medium">{day.present}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">{day.absent}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm font-medium">{day.half_day}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm font-medium">{day.on_leave}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm font-medium">{day.holiday}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-medium">{day.week_off}</span>
                        </td>
                        <td className="p-3 text-center font-medium">{day.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {summaryData && activeTab === 'byDept' && (
          <Card>
            <CardHeader>
              <CardTitle>Department-wise Attendance ({summaryData.by_department.length} departments)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Department</th>
                      <th className="text-center p-3 text-sm font-medium text-emerald-600">Present</th>
                      <th className="text-center p-3 text-sm font-medium text-red-600">Absent</th>
                      <th className="text-center p-3 text-sm font-medium text-amber-600">Half Day</th>
                      <th className="text-center p-3 text-sm font-medium text-indigo-600">On Leave</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Total</th>
                      <th className="text-center p-3 text-sm font-medium text-teal-600">Attendance Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {summaryData.by_department.map(dept => {
                      const workingDays = dept.total - dept.holiday - dept.week_off;
                      const attended = dept.present + (dept.half_day * 0.5);
                      const rate = workingDays > 0 ? Math.round((attended / workingDays) * 100) : 0;
                      
                      return (
                        <tr key={dept.department} className="hover:bg-gray-50">
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-gray-400" />
                              <span className="font-medium">{dept.department}</span>
                            </div>
                          </td>
                          <td className="p-3 text-center">
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm font-medium">{dept.present}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">{dept.absent}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm font-medium">{dept.half_day}</span>
                          </td>
                          <td className="p-3 text-center">
                            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-sm font-medium">{dept.on_leave}</span>
                          </td>
                          <td className="p-3 text-center font-medium">{dept.total}</td>
                          <td className="p-3 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              rate >= 90 ? 'bg-emerald-100 text-emerald-700' :
                              rate >= 75 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {rate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default AttendanceSummary;
